import { useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import { useApi } from "../../hooks/useApi";
import { listLeads, createLead, updateLead, deleteLead } from "../../api/leadService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate, formatDateTime } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

const STATUSES = [
  { value: "new", label: "New", badge: "badge-info" },
  { value: "contacted", label: "Contacted", badge: "badge-warning" },
  { value: "demo_scheduled", label: "Demo scheduled", badge: "badge-purple" },
  { value: "won", label: "Won", badge: "badge-success" },
  { value: "lost", label: "Lost", badge: "badge-danger" },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || { label: v, badge: "badge-neutral" };

// Backend timestamp → <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm").
const toLocalInput = (v) => (v ? String(v).replace(" ", "T").slice(0, 16) : "");

export default function LeadsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const list = useApi(() => listLeads({ page, ...(status ? { status } : {}) }), [page, status]);
  const [editing, setEditing] = useState(null); // row | "new" | null

  const rows = paginatedRows(list.data);
  const meta = paginatedMeta(list.data);

  const remove = async (l) => {
    if (!window.confirm(`Delete lead "${l.name}"? This can't be undone.`)) return;
    try {
      await deleteLead(l.id);
      showToast("success", "Lead deleted");
      list.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Leads</h1>
          <div className="page-sub">Enquiries from the website plus leads you log manually.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            <Icon name="plus" size={14} /> New lead
          </button>
        </div>
      </div>

      <div className="row gap-sm" style={{ marginBottom: 12, flexWrap: "wrap" }}>
        <button className={`chip ${status === "" ? "active" : ""}`} onClick={() => { setStatus(""); setPage(1); }}>All</button>
        {STATUSES.map((s) => (
          <button key={s.value} className={`chip ${status === s.value ? "active" : ""}`} onClick={() => { setStatus(s.value); setPage(1); }}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {list.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : rows.length === 0 ? (
            <EmptyState icon="inbox" title="No leads yet" hint="Enquiries from the landing page show up here." />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Firm</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Follow-up</th>
                  <th>Source</th>
                  <th className="t-num">Created</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const sm = statusMeta(l.status);
                  return (
                    <tr key={l.id} className="clickable" onClick={() => setEditing(l)}>
                      <td className="t-primary fw-500">{l.name}</td>
                      <td className="t-muted">{l.firm_name || "—"}</td>
                      <td className="t-muted">{l.email || l.phone || "—"}</td>
                      <td><span className={`badge ${sm.badge}`}>{sm.label}</span></td>
                      <td className="t-muted">{l.follow_up_at ? formatDateTime(l.follow_up_at) : "—"}</td>
                      <td><span className="badge badge-neutral">{l.source === "manual" ? "Manual" : "Website"}</span></td>
                      <td className="t-num t-muted">{formatDate(l.created_at)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="row gap-sm">
                          <button className="icon-btn" title="Edit" onClick={() => setEditing(l)}>
                            <Icon name="edit" size={13} />
                          </button>
                          <button className="icon-btn" title="Delete" style={{ color: "var(--danger)" }} onClick={() => remove(l)}>
                            <Icon name="trash" size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <Pagination meta={meta} page={page} onPageChange={setPage} disabled={list.loading} />
        </div>
      </div>

      {editing && (
        <LeadDrawer
          key={editing === "new" ? "new" : editing.id}
          lead={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); list.reload(); }}
        />
      )}
    </>
  );
}

function LeadDrawer({ lead, onClose, onSaved }) {
  const isEdit = !!lead;
  const [form, setForm] = useState(() => ({
    name: lead?.name ?? "",
    firm_name: lead?.firm_name ?? "",
    email: lead?.email ?? "",
    phone: lead?.phone ?? "",
    message: lead?.message ?? "",
    status: lead?.status ?? "new",
    follow_up_at: toLocalInput(lead?.follow_up_at),
    notes: lead?.notes ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        firm_name: form.firm_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
        status: form.status,
        follow_up_at: form.follow_up_at ? form.follow_up_at.replace("T", " ") : null,
        notes: form.notes.trim() || null,
      };
      if (isEdit) {
        await updateLead(lead.id, payload);
        showToast("success", "Lead updated");
      } else {
        await createLead(payload);
        showToast("success", "Lead added");
      }
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isEdit ? "Lead" : "New lead"}
      subtitle={isEdit && lead.source === "landing" ? "From the website" : undefined}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !form.name.trim()}>
            {saving ? "Saving…" : isEdit ? "Save" : "Add lead"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
        </div>
        <div className="field">
          <label className="field-label">Firm name</label>
          <input className="input" value={form.firm_name} onChange={(e) => set({ firm_name: e.target.value })} />
        </div>
        <div className="row gap-sm">
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Email</label>
            <input className="input" value={form.email} onChange={(e) => set({ email: e.target.value })} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Phone</label>
            <input className="input" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </div>
        </div>
        <div className="field">
          <label className="field-label">Message</label>
          <textarea className="textarea" rows={3} value={form.message} onChange={(e) => set({ message: e.target.value })} />
        </div>
        <div className="row gap-sm">
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Status</label>
            <select className="select" value={form.status} onChange={(e) => set({ status: e.target.value })}>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Follow-up</label>
            <input className="input" type="datetime-local" value={form.follow_up_at} onChange={(e) => set({ follow_up_at: e.target.value })} />
          </div>
        </div>
        <div className="field">
          <label className="field-label">Notes <span className="t-muted text-xs">(internal)</span></label>
          <textarea className="textarea" rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Call outcomes, next steps…" />
        </div>
      </div>
    </Drawer>
  );
}
